import { useEffect, useRef, useState } from "react";
import { paper, Point, Size, Rectangle, Path, Segment, PointText } from "paper";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import Debug from "./Debug";
import { Button, Modal, Form } from "react-bootstrap";
import { colors } from "../../../helpers/colors";
import { useToasts } from "react-toast-notifications";
import DownloadMenu from "../../shared/DownloadMenu";
import {
  TRUESPACE_LABEL,
  openTrueSpace,
  projectPointFromEvent,
  hitsLink,
} from "../../shared/trueSpace";
import { computeAsf } from "../../shared/asf";
import { correctApparentDip } from "../../shared/inclination";
import { FLAT_INCLINED_ALERT } from "../utils/horizontalTrace";
import { toStartOffset, scaleLabelChainage, chainageToCanvasX } from "../../shared/chainage";
import {
  alphaBetween,
  transverseSign,
  crossSectionPoint,
  solveAlphaZero,
  solveLevel,
  solveVertical,
  solveParallelTrace,
  alphaZeroInclined,
  polylineExtent,
  polylineLength,
  perimeterYlog,
  BETA_ZERO_TOLERANCE,
  ALPHA0_INVERT_ALERT,
  ALPHA0_PARALLEL_NOTE,
  coincidenceAlert,
  FLAT_PARALLEL_NOTE,
  AXIS_PARALLEL_NOTE,
  VERTICAL_CENTRELINE_ALERT,
  INVISIBLE_INVERT_ALERT,
  INVISIBLE_SPL_ALERT,
  INVISIBLE_CROWN_ALERT,
} from "../../shared/alphaZero";
import { effectiveWallHeight, isBoxSection } from "../../shared/section";
import { createTraceAlerts, changedTraces, CLIP_ALERT, drawnPastLog } from "../../shared/traceAlerts";
import { labelCell } from "../../shared/labelField";
import { faceLogPointRow } from "../../shared/faceLogRows";
import { faceLogSecondPoint } from "../../shared/orientationSources";
import { drawTunnelHeader } from "../../shared/tunnelHeader";
import { chainageExtent, fullLogExtent, NO_EXTENT } from "../../shared/traceExtent";

const LOG_LINE_ALERTS = [INVISIBLE_INVERT_ALERT, INVISIBLE_SPL_ALERT, INVISIBLE_CROWN_ALERT];

/**
 * Output Component
 * Handles trace visualization and all mathematical calculations
 * Implements optimized P6 calculation algorithm
 */
const Output = (props) => {
  let { addToast, removeAllToasts } = useToasts();
  const [uservariables, setUserVariables] = useState([]);
  const [show, setShow] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState(false);
  const handleClose = () => setShow(false);

  // Common variables from first trace dimensions
  let tracedimensions = props.dimensions[0];
  let multiplier = 5;
  let padding = 110;
  let points = 60;
  let length = (tracedimensions.endrd - tracedimensions.startrd) * multiplier;
  // Chainage origin: x=padding represents the Start Chainage, not 0. Traces are
  // anchored relative to this offset and the scale prints absolute chainages.
  let startOffsetRD = toStartOffset(tracedimensions.startrd);
  let height = tracedimensions.height * multiplier;
  let width = tracedimensions.width * multiplier;
  let hw = effectiveWallHeight(tracedimensions) * multiplier;
  let ws = (tracedimensions.width) * multiplier;

  // Base computations
  let Whalf = ws / 2;
  let HC = height - hw;
  let r = (Math.pow(Whalf, 2) + Math.pow(HC, 2)) / (2 * HC);
  let Theta = toDegree(Math.atan((r - HC) / Whalf));
  let Arc = (Math.PI * r) / 2 - ((Math.PI * r) / 2) * (Theta / 90);
  let ArcFull = Arc * 2;
  let HWn = height - r;
  // NEW: Gama calculation for P6
  // Gama = Cos⁻¹((W/2)/R) where W and R are in meters
  let Gama = toDegree(Math.acos((tracedimensions.width / 2) / (r / multiplier)));
  let Ymax = padding + 2 * (hw + Arc);
  let usrvar = [];

  //storing the legend path
  let legendpath = {};
  /**
   * Convert degrees to radians
   */
  function toRadians(angle) {
    return angle * (Math.PI / 180);
  }

  /**
   * Convert radians to degrees
   */
  function toDegree(radian) {
    return radian * (180 / Math.PI);
  }

  /**
   * Calculate Alpha (angle between strike and tunnel direction)
   */
  const calculateAlpha = (D, Dir) => {
    let Dnew = D > 180 ? D - 180 : D;
    let Dirnew = Dir > 180 ? Dir - 180 : Dir;
    let Value = Math.abs(Dnew - Dirnew);

    if (Value <= 90) {
      return 90 - Value;
    } else {
      return Value - 90;
    }
  };

  /**
   * Calculate Beta (apparent dip along tunnel)
   */
  const calculateBeta = (amt, Alpha) => {
    let TanBeta = Math.tan(toRadians(amt)) * Math.sin(toRadians(Alpha));
    return toDegree(Math.atan(TanBeta));
  };

  /**
   * Calculate quadrants Q1-Q4 based on tunnel direction D
   */
  const calculateQuadrants = (D) => {
    let Q1max, Q1min, Q2max, Q2min, Q3max, Q3min, Q4max, Q4min;
    let Q1temp, Q2temp, Q3temp, Q4temp;

    // Quadrant 1
    Q1max = D;
    Q1min = D - 90;
    if (Q1min < 0) {
      Q1temp = 360 + Q1min;
      Q1min = Q1temp;
    }

    // Quadrant 2
    Q2min = D;
    Q2temp = D + 90;
    if (Q2temp > 360) {
      Q2max = Q2temp - 360;
    } else {
      Q2max = Q2temp;
    }

    // Quadrant 3
    Q3min = Q2max;
    Q3temp = Q3min + 90;
    if (Q3temp > 360) {
      Q3max = Q3temp - 360;
    } else {
      Q3max = Q3temp;
    }

    // Quadrant 4
    Q4min = Q3max;
    Q4temp = Q4min + 90;
    if (Q4temp > 360) {
      Q4max = Q4temp - 360;
    } else {
      Q4max = Q4temp;
    }

    return { Q1min, Q1max, Q2min, Q2max, Q3min, Q3max, Q4min, Q4max };
  };

  /**
   * Calculate Dopp (direction opposite to tunnel direction)
   */
  const calculateDopp = (D) => {
    if (D <= 180) {
      return D + 180;
    } else {
      return D - 180;
    }
  };

  /**
   * Calculate ApDir (apparent direction)
   */
  const calculateApDir = (D, Dir, quadrants) => {
    let Dopp = calculateDopp(D);
    let { Q1min, Q1max, Q2min, Q2max, Q3min, Q3max, Q4min, Q4max } =
      quadrants;

    if (Dir == D) {
      return D;
    }

    // Dopp is 360 for a due-south drive, which Dir 0 must match.
    if (Dir % 360 === Dopp % 360) {
      return Dopp;
    }

    // Check quadrants
    if (
      (Q1min < Dir && Dir < Q1min + 90) ||
      (Dir < Q1max && Dir > D - 90)
    ) {
      return D;
    }

    if (
      (Q2min < Dir && Dir < Q2min + 90) ||
      (Dir < Q2max && Dir > Q2max - 90)
    ) {
      return D;
    }

    if (
      (Q3min < Dir && Dir < Q3min + 90) ||
      (Dir < Q3max && Dir > Q3max - 90)
    ) {
      return Dopp;
    }

    if (
      (Q4min < Dir && Dir < Q4min + 90) ||
      (Dir < Q4max && Dir > Q4max - 90)
    ) {
      return Dopp;
    }

    return D; // Default
  };

  /**
   * Calculate P6 for Right Side
   * According to algorithm: Calculate P1 first, then derive XP3 and XPmid from XP1
   * All inputs: rd, d, Hp are in meters (not multiplied)
   */
  const calculateP6RightSide = (
    rd,
    d,
    Hp,
    D,
    Dir,
    amt,
    Alpha,
    TanAlpha,
    TanBeta,
    quadrants,
    ApDir,
    tunnelWidth,
    leanFlipped = false
  ) => {
    let Dopp = calculateDopp(D);
    let { Q1min, Q1max, Q2min, Q2max, Q3min, Q3max, Q4min, Q4max } =
      quadrants;

    // Calculate d' = Hp / tan(Beta) - result in meters (RD units). The
    // quadrant signs below assume the level-tunnel lean; an inclination that
    // folds the apparent dip past 90° reverses it.
    let dprime = (leanFlipped ? -1 : 1) * Hp / TanBeta;

    // Calculate d" = (W/2) - d - in meters
    let ddoubleprime = tunnelWidth / 2 - d;

    // Calculate Off = d" / tan(Alpha) - result in meters (RD units)
    let Off = ddoubleprime / TanAlpha;

    // Calculate P1 based on Dir and quadrant
    let P1;
    if (Dir == D) {
      P1 = rd + dprime + Off;
    } else if (Dir == Dopp) {
      P1 = rd + dprime + Off;
    } else if (Q1min < Dir && Dir < Q1max) {
      // Dir is in Q1
      P1 = rd + dprime + Off;
    } else if (Q2min < Dir && Dir < Q2max) {
      // Dir is in Q2
      P1 = rd + dprime - Off;
    } else if (Q3min < Dir && Dir < Q3max) {
      // Dir is in Q3
      P1 = rd + dprime + Off;
    } else if (Q4min < Dir && Dir < Q4max) {
      // Dir is in Q4
      P1 = rd - dprime - Off;
    } else {
      P1 = rd + dprime + Off; // Default
    }

    // XP1 = P1, YP1 = 0
    let XP1 = padding + P1 * multiplier;
    let YP1 = Ymax;

    // Derive XP3 and XPmid from XP1
    let XP3, XPmid;

    if (Dir == D) {
      XP3 = XP1;
      XPmid = XP1;
    } else if (Dir == Dopp) {
      XP3 = XP1;
      XPmid = XP1;
    } else if (
      (Q1min < Dir && Dir < Q1min + 90) ||
      (Dir < Q1max && Dir > D - 90)
    ) {
      XP3 = XP1 - (tunnelWidth * multiplier) / TanAlpha;
      XPmid = XP1 - Math.abs(XP1 - XP3) / 2;
    } else if (
      (Q2min < Dir && Dir < Q2min + 90) ||
      (Dir < Q2max && Dir > Q2max - 90)
    ) {
      XP3 = XP1 + (tunnelWidth * multiplier) / TanAlpha;
      XPmid = XP1 + Math.abs(XP1 - XP3) / 2;
    } else if (
      (Q3min < Dir && Dir < Q3min + 90) ||
      (Dir < Q3max && Dir > Q3max - 90)
    ) {
      XP3 = XP1 - (tunnelWidth * multiplier) / TanAlpha;
      XPmid = XP1 - Math.abs(XP1 - XP3) / 2;
    } else if (
      (Q4min < Dir && Dir < Q4min + 90) ||
      (Dir < Q4max && Dir > Q4max - 90)
    ) {
      XP3 = XP1 + (tunnelWidth * multiplier) / TanAlpha;
      XPmid = XP1 + Math.abs(XP1 - XP3) / 2;
    } else {
      XP3 = XP1;
      XPmid = XP1;
    }

    let YP3 = Ymax - (2 * hw + ArcFull);

    return {
      P1: new Point(XP1, YP1),
      P3: new Point(XP3, YP3),
      XPmid: XPmid,
    };
  };

  /**
   * Calculate P6 for Left Side
   * According to algorithm: Calculate P3 first, then derive XP1 and XPmid from XP3
   * All inputs: rd, d, Hp are in meters (not multiplied)
   */
  const calculateP6LeftSide = (
    rd,
    d,
    Hp,
    D,
    Dir,
    amt,
    Alpha,
    TanAlpha,
    TanBeta,
    quadrants,
    ApDir,
    tunnelWidth,
    leanFlipped = false
  ) => {
    let Dopp = calculateDopp(D);
    let { Q1min, Q1max, Q2min, Q2max, Q3min, Q3max, Q4min, Q4max } =
      quadrants;

    // Calculate d' = Hp / tan(Beta) - result in meters (RD units). The
    // quadrant signs below assume the level-tunnel lean; an inclination that
    // folds the apparent dip past 90° reverses it.
    let dprime = (leanFlipped ? -1 : 1) * Hp / TanBeta;

    // Calculate d" = (W/2) - d - in meters
    let ddoubleprime = tunnelWidth / 2 - d;

    // Calculate Off = d" / tan(Alpha) - result in meters (RD units)
    let Off = ddoubleprime / TanAlpha;

    // Calculate P3 based on Dir and quadrant
    let P3;
    if (Dir == D) {
      P3 = rd + dprime - Off;
    } else if (Dir == Dopp) {
      P3 = rd - dprime - Off;
    } else if (Q1min < Dir && Dir < Q1max) {
      // Dir is in Q1
      P3 = rd + dprime - Off;
    } else if (Q2min < Dir && Dir < Q2max) {
      // Dir is in Q2
      P3 = rd + dprime + Off;
    } else if (Q3min < Dir && Dir < Q3max) {
      // Dir is in Q3
      P3 = rd - dprime - Off;
    } else if (Q4min < Dir && Dir < Q4max) {
      // Dir is in Q4
      P3 = rd - dprime + Off;
    } else {
      P3 = rd + dprime - Off; // Default
    }

    // XP3 = P3, YP3 = (2 × HW) + ArcFull
    let XP3 = padding + P3 * multiplier;
    let YP3 = Ymax - (2 * hw + ArcFull);

    // Derive XP1 and XPmid from XP3
    let XP1, XPmid;

    if (Dir == D) {
      XP1 = XP3;
      XPmid = XP3;
    } else if (Dir == Dopp) {
      XP1 = XP3;
      XPmid = XP3;
    } else if (
      (Q1min < Dir && Dir < Q1min + 90) ||
      (Dir < Q1max && Dir > D - 90)
    ) {
      XP1 = XP3 + (tunnelWidth * multiplier) / TanAlpha;
      XPmid = XP3 + Math.abs(XP1 - XP3) / 2;
    } else if (
      (Q2min < Dir && Dir < Q2min + 90) ||
      (Dir < Q2max && Dir > Q2max - 90)
    ) {
      XP1 = XP3 - (tunnelWidth * multiplier) / TanAlpha;
      XPmid = XP3 - Math.abs(XP1 - XP3) / 2;
    } else if (
      (Q3min < Dir && Dir < Q3min + 90) ||
      (Dir < Q3max && Dir > Q3max - 90)
    ) {
      XP1 = XP3 + (tunnelWidth * multiplier) / TanAlpha;
      XPmid = XP1 - Math.abs(XP1 - XP3) / 2;
    } else if (
      (Q4min < Dir && Dir < Q4min + 90) ||
      (Dir < Q4max && Dir > Q4max - 90)
    ) {
      XP1 = XP3 - (tunnelWidth * multiplier) / TanAlpha;
      XPmid = XP1 + Math.abs(XP1 - XP3) / 2;
    } else {
      XP1 = XP3;
      XPmid = XP3;
    }

    let YP1 = Ymax;

    return {
      P1: new Point(XP1, YP1),
      P3: new Point(XP3, YP3),
      XPmid: XPmid,
    };
  };

  const updateUserVariable = (usrvar) => {
    setUserVariables(usrvar);
  };

  // Box of the TrueSpace link drawn in the output notes, in project
  // coordinates. Set while drawing; read by the canvas mouse handlers below.
  const trueSpaceLinkBounds = useRef(null);

  const canvasProjectPoint = (e) => {
    const view = paper.view;
    if (!view) return null;
    return projectPointFromEvent(
      e,
      e.currentTarget.getBoundingClientRect(),
      view.viewSize.width
    );
  };

  const handleCanvasClick = (e) => {
    if (hitsLink(canvasProjectPoint(e), trueSpaceLinkBounds.current)) {
      openTrueSpace();
    }
  };

  const handleCanvasMouseMove = (e) => {
    const over = hitsLink(canvasProjectPoint(e), trueSpaceLinkBounds.current);
    e.currentTarget.style.cursor = over ? "pointer" : "default";
  };

  useEffect(() => {
    if (!props.showdebug) {
      paper.setup("paper-canvas");
      creategraph();
    }
  }, [props.dimensions, props.showdebug]);

  /**
   * Create scale markings on the log
   */
  const createScale = () => {
    let scalearr = [];
    let tempYmax = tracedimensions.iscrown
      ? padding + 2 * Arc + hw
      : Ymax;

    for (let i = padding; i <= length + padding; i += multiplier * 10) {
      let text = new PointText(new Point(i, tempYmax + 15));
      text.justification = "center";
      text.fillColor = "black";
      text.content = scaleLabelChainage(i, padding, multiplier, startOffsetRD);
      scalearr.push([i, tempYmax + 10]);
      scalearr.push([i, tempYmax]);
      if (i + multiplier * 10 <= length + padding) {
        for (let j = 1; j <= 9; j++) {
          scalearr.push([i + multiplier * j, tempYmax]);
          scalearr.push([i + multiplier * j, tempYmax + 5]);
          scalearr.push([i + multiplier * j, tempYmax]);
        }
        scalearr.push([i + multiplier * 10, tempYmax]);
      }
    }
    new Path({
      segments: scalearr,
      strokeColor: "black",
      strokeWidth: 1,
    });
  };

  /**
   * Create tunnel log structure
   */
  const createLog = () => {
    let style = {
      fillColor: "white",
      strokeColor: "black",
      strokeWidth: 1,
    };

    let path, path1;
    if (!tracedimensions.iscrown) {
      path = new Path.Rectangle(
        new Rectangle(
          new Point(padding, padding),
          new Size(length, 2 * (hw + Arc))
        )
      );
      path.style = style;
      path1 = new Path.Rectangle(
        new Rectangle(
          new Point(padding, padding + hw),
          new Size(length, 2 * Arc)
        )
      );
      path1.style = style;
    } else {
      path = new Path.Rectangle(
        new Rectangle(
          new Point(padding, padding + hw),
          new Size(length, 2 * Arc)
        )
      );
      path.style = style;
    }

    var path2 = new Path.Rectangle(
      new Rectangle(
        path.bounds.rightCenter.add([HC, -width / 2]),
        new Size(hw, width)
      )
    );
    path2.style = style;
    legendpath["path2"] = path2;
    var path21 = new Path.Arc(
      path2.bounds.topLeft,
      path.bounds.rightCenter,
      path2.bounds.bottomLeft
    );
    path21.style = style;

    // Central line
    var path3 = new Path.Line(path.bounds.leftCenter, path2.bounds.rightCenter);
    path3.strokeColor = "black";
    path3.dashArray = [4, 10];


    // Dashed lines for tunnel structure
    if (!tracedimensions.iscrown) {
      var path4 = new Path(
        new Segment(path.bounds.topRight),
        new Segment(path2.bounds.topRight)
      );
      path4.strokeColor = "black";
      path4.dashArray = [14, 5];
      var path5 = new Path(
        new Segment(path.bounds.bottomRight),
        new Segment(path2.bounds.bottomRight)
      );
      path5.strokeColor = "black";
      path5.dashArray = [14, 5];
      var path6 = new Path(
        new Segment(path1.bounds.topRight),
        new Segment(path2.bounds.topLeft)
      );
      path6.strokeColor = "black";
      path6.dashArray = [14, 5];
      var path7 = new Path(
        new Segment(path1.bounds.bottomRight),
        new Segment(path2.bounds.bottomLeft)
      );
      path7.strokeColor = "black";
      path7.dashArray = [14, 5];
    } else {
      var path4 = new Path(
        new Segment(path.bounds.topRight),
        new Segment(path2.bounds.topLeft)
      );
      path4.strokeColor = "black";
      path4.dashArray = [3, 5];
      var path5 = new Path(
        new Segment(path.bounds.bottomRight),
        new Segment(path2.bounds.bottomLeft)
      );
      path5.strokeColor = "black";
      path5.dashArray = [3, 5];
    }

    // Labels
    if (!tracedimensions.iscrown) {
      let text = new PointText(path.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Left Invert";
      text = new PointText(path.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Right Invert";
      text = new PointText(path1.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Left Spring Level";
      text = new PointText(path1.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Right Spring Level";
    } else {
      let text = new PointText(path.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Left Spring Level";
      text = new PointText(path.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Right Spring Level";
    }

    let text = new PointText(path.bounds.leftCenter.add([-5, 5]));
    text.justification = "right";
    text.fillColor = "black";
    text.content = "Centerline";

    drawTunnelHeader({ Path, Point, PointText, bounds: path.bounds, direction: tracedimensions.direction, inclination: tracedimensions.inclination, style });
  };

  /**
   * Create trace for a single discontinuity
   * Implements Subroutine-1 (ApDir = D) and Subroutine-2 (ApDir = Dopp)
   */
  const createTrace = () => {
    // Store base computations in user variables
    usrvar["Length"] = length / multiplier;
    usrvar["Height"] = height / multiplier;
    usrvar["Width"] = width / multiplier;
    usrvar["HW"] = hw / multiplier;
    usrvar["WS"] = ws / multiplier;
    usrvar["Whalf"] = Whalf / multiplier;
    usrvar["HC"] = HC / multiplier;
    usrvar["R"] = r / multiplier;
    usrvar["Theta"] = Theta;
    usrvar["Arc"] = Arc / multiplier;
    usrvar["ArcFull"] = ArcFull / multiplier;
    usrvar["Gama"] = Gama; // NEW

    const orientationLocationMap = {
      leftinvert: "Left Invert",
      leftspl: "Left SPL",
      rightinvert: "Right Invert",
      rightspl: "Right SPL",
      centerline: "Centreline",
      otherpoint: "",
    };
    const tunnelSideLabelMap = { left: "Left", right: "Right", center: "Center" };
    // d = 0 is the centreline whichever side was named; must match the plot.
    const tunnelSideLabel = (dimension) =>
      parseFloat(dimension.distancefromcenter) === 0
        ? tunnelSideLabelMap.center
        : tunnelSideLabelMap[dimension.tunnelside];
    // A trace whose orientation came from known chainages has no face-heading
    // point to report; the three chainages the user entered are the useful
    // record, so they take those columns and the sub-group is relabelled.
    const isChainageSource = (dimension) =>
      (dimension.orientationsource || "known") === "chainages";
    const chainageCell = (v) =>
      v === undefined || v === null || v === "" ? "-" : String(v);

    // Trailing ".0" is kept so the chainage column stays aligned.
    const oneDecimal = (value) => {
      const n = Number(value);
      return value === "-" || value === undefined || !isFinite(n)
        ? "-"
        : n.toFixed(1);
    };
    const inputRows = [];
    const outputRows = [];
    // Distinct horizontal-trace alerts collected over all discontinuities; raised
    // once each after the loop so identical messages aren't repeated per trace.
    const horizontalAlerts = createTraceAlerts();
    const asfAlerts = createTraceAlerts();
    const clipAlerts = createTraceAlerts();

    const firstOtherpointIndex = props.dimensions.findIndex(d => (d.orientation || "leftinvert") === "otherpoint");

    // The module-level geometry is in canvas units; the cross-section solvers
    // in utils/alphaZero work in metres.
    const geomM = {
      W: width / multiplier,
      WS: ws / multiplier,
      HW: hw / multiplier,
      H: height / multiplier,
      R: r / multiplier,
      HWn: HWn / multiplier,
      Arc: Arc / multiplier,
      ArcFull: ArcFull / multiplier,
      Theta,
    };
    const logStartRD = startOffsetRD;
    const logEndRD = Number(tracedimensions.endrd);
    const fmt1 = (v) => Number(v).toFixed(1).replace(/\.0$/, "");

    // One Inputs-table row per trace, the same for every plotting branch.
    // Tunnel-surface rows show their location name; only Face (Heading)
    // rows ("Any Other Point") carry side / distance / height data.
    const buildInputRow = (dimension, index, DirInput, amtInput) => {
      const orientation = dimension.orientation || "leftinvert";
      const isP6 = orientation === "otherpoint";
      const p6Cell = (v) => (isP6 && v !== undefined && v !== "" ? String(v) : "-");
      const row = {
        no: String(index + 1),
        label: labelCell(dimension),
        dir: String(DirInput),
        amt: String(amtInput),
        ch: String(dimension.rd),
        surface: orientationLocationMap[orientation] || "-",
        side: isP6 ? tunnelSideLabel(dimension) || "-" : "-",
        dist: p6Cell(dimension.distancefromcenter),
        ht: p6Cell(dimension.verticalheight),
        color: colors[index],
        ...(isChainageSource(dimension)
          ? {
              source: "chainages",
              side: chainageCell(dimension.p4),
              dist: chainageCell(dimension.p2),
              ht: chainageCell(dimension.p5),
            }
          : { source: dimension.orientationsource || "known" }),
      };
      // Face logs carry a known point on each face; both are inputs.
      if ((dimension.orientationsource || "known") !== "facelogs") return [row];
      return [
        { ...row, ...faceLogPointRow(dimension, 1) },
        // The second line belongs to the same discontinuity: no repeat of its
        // number, label or orientation.
        {
          no: " ",
          label: " ",
          dir: " ",
          amt: " ",
          ...faceLogPointRow(dimension, 2),
          color: colors[index],
          source: "facelogs",
        },
      ];
    };

    // One Outputs-table row per trace. A chainage is "-" where the trace has
    // no single crossing of that line.
    const pushOutputRow = (index, row) => {
      outputRows.push({
        no: String(index + 1),
        angle: fmt1(row.alpha),
        adip: fmt1(row.adip),
        adipi: fmt1(row.adipi),
        chli: oneDecimal(row.P3x),
        chri: oneDecimal(row.P1x),
        chlspl: oneDecimal(row.P4x),
        chrspl: oneDecimal(row.P2x),
        chcl: oneDecimal(row.P5x),
        tsp: String(row.span),
        ...(row.extent || NO_EXTENT),
        asf: Number(row.asf).toFixed(2).replace(/\.?0+$/, ""),
        color: colors[index],
      });
    };

    // Terminate the trace exactly at the limits of the log
    // (left = padding, right = padding + length). A near-horizontal trace can
    // wrap the unrolled tunnel surface and cross the right boundary many
    // times, so a rectangular clip mask clips the trace to the log regardless
    // of how many times it crosses.
    const clipToLog = (union, index) => {
      const exceedsLog = drawnPastLog(union.bounds.left, union.bounds.right, {
        padding, multiplier, startOffsetRD, length,
      });
      const clipRect = new Path.Rectangle(
        new Rectangle(new Point(padding, -100000), new Size(length, 200000))
      );
      const traceClip = new paper.Group([clipRect, union]);
      clipRect.clipMask = true;
      traceClip.name = "traceClip-" + index;
      if (exceedsLog) {
        clipAlerts.add(CLIP_ALERT, index);
      }
    };

    // The data-point marker for a named location sits on that location's
    // line at the point's own chainage.
    const namedLineY = {
      rightinvert: 0,
      rightspl: hw,
      centerline: hw + Arc,
      leftspl: hw + ArcFull,
      leftinvert: 2 * hw + ArcFull,
    };
    const markNamedPoint = (orientation, RD0, index) => {
      if (!(orientation in namedLineY)) return;
      const circle = new Path.Circle(
        new Point(
          chainageToCanvasX(RD0, padding, multiplier, startOffsetRD),
          Ymax - namedLineY[orientation]
        ),
        2
      );
      circle.style = { fillColor: colors[index], strokeColor: colors[index], strokeWidth: 1 };
    };

    // "Any Other Point": the Tunnel Face line at the data point's chainage,
    // and the point itself on the face sketch.
    const markFacePoint = (dimension, index, style, labelled = true) => {
      const P1y = Ymax;
      const P3y = Ymax - (2 * hw + ArcFull);
      // d = 0 is the centreline whichever side was named.
      const p6Side =
        parseFloat(dimension.distancefromcenter) === 0 ? "center" : dimension.tunnelside;
      // The Tunnel Face marker sits at the data point's chainage.
      const P6x = chainageToCanvasX(dimension.rd, padding, multiplier, startOffsetRD);
      let tunnelFaceDottedLine = new Path.Line(
        new Point(P6x, P1y),
        new Point(P6x, P3y)
      );
      tunnelFaceDottedLine.style = { ...style, dashArray: [3, 3] };
      tunnelFaceDottedLine.strokeColor = colors[index];
      let tunnelFaceDottedLineArrowLeft = new Path.Line(
        new Point(P6x, P3y-2),
        new Point(P6x - 3, P3y - 5)
      );
      tunnelFaceDottedLineArrowLeft.style = { ...style};
      tunnelFaceDottedLineArrowLeft.strokeColor = colors[index];
      let tunnelFaceDottedLineArrowRight = new Path.Line(
        new Point(P6x, P3y-2),
        new Point(P6x + 3, P3y - 5)
      );
      tunnelFaceDottedLineArrowRight.style = { ...style };
      tunnelFaceDottedLineArrowRight.strokeColor = colors[index];
      let tunnelFaceDottedLineArrowDown = new Path.Line(
        new Point(P6x, P3y-8),
        new Point(P6x, P3y - 5)
      );
      tunnelFaceDottedLineArrowDown.style = { ...style};
      tunnelFaceDottedLineArrowDown.strokeColor = colors[index];

      // Two faces a few metres apart would print their labels over each other.
      let tunnelFaceText = new PointText(new Point(P6x-20, P3y - (labelled ? 10 : 20)));
      tunnelFaceText.content = "Tunnel Face";
      tunnelFaceText.style = {  fontSize: 8 };
      tunnelFaceText.fillColor = colors[index];
      let path2center = legendpath["path2"].bounds.rightCenter;
      if (p6Side === "right" || p6Side === "center") {
        path2center = new Point(path2center.x-parseFloat(dimension.verticalheight)*multiplier, path2center.y+parseFloat(dimension.distancefromcenter)*multiplier);
      } else {
        path2center = new Point(path2center.x-parseFloat(dimension.verticalheight)*multiplier, path2center.y-parseFloat(dimension.distancefromcenter)*multiplier);
      }
      let tunnelDotOnLegend = new Path.Circle(path2center, 2);
      tunnelDotOnLegend.style = { ...style, fillColor: colors[index] };
      if(labelled && index === firstOtherpointIndex) {
        let linepath2center = new Path.Line(path2center, new Point(path2center.x+10, path2center.y-30));
        linepath2center.style = { ...style, dashArray: [3, 3]};
        linepath2center.strokeColor = colors[index];
        let linepath2centerText = new PointText(new Point(path2center.x+10, path2center.y-30));
        linepath2centerText.content = "Discontinuity data location";
        linepath2centerText.style = {  fontSize: 8 };
        linepath2centerText.fillColor = colors[index];
      }
    };

    // Known chainages anchor at the centreline; the SPL chainages entered
    // alongside it are data points too.
    const markKnownChainages = (dimension, index) => {
      if ((dimension.orientationsource || "known") !== "chainages") return;
      markNamedPoint("leftspl", Number(dimension.p4), index);
      markNamedPoint("rightspl", Number(dimension.p2), index);
    };

    // The data-point marker, whichever way the trace was plotted. A face point
    // that lies on the tunnel surface is also dotted on the log, like a named one.
    const markDataPoint = (dimension, index, style) => {
      const orientation = dimension.orientation || "leftinvert";
      markKnownChainages(dimension, index);
      if (orientation !== "otherpoint") {
        markNamedPoint(orientation, Number(dimension.rd), index);
        return;
      }
      markFaceAndSurface(dimension, index, style, true);
      // Face logs carry a known point on each face; both are data locations.
      const second = faceLogSecondPoint(dimension);
      if (second) markFaceAndSurface({ ...dimension, ...second }, index, style, false);
    };

    const markFaceAndSurface = (dimension, index, style, labelled) => {
      markFacePoint(dimension, index, style, labelled);
      const d = parseFloat(dimension.distancefromcenter) || 0;
      const ylog = perimeterYlog(
        dimension.tunnelside === "left" ? -d : d,
        parseFloat(dimension.verticalheight) || 0,
        geomM
      );
      if (ylog === null) return;
      const circle = new Path.Circle(
        new Point(chainageToCanvasX(dimension.rd, padding, multiplier, startOffsetRD), Ymax - ylog * multiplier),
        2
      );
      circle.style = { fillColor: colors[index], strokeColor: colors[index], strokeWidth: 1 };
    };


    props.dimensions.forEach((dimension, index) => {
      let style = {
        fillColor: "transparent",
        strokeColor: colors[index],
        strokeWidth: 1,
      };

      let rd;
      let traceunion = [];
      let Xmin, Xmax;
      // The degenerate-case gates below test their angle for exactly 0, which
      // is only ever true of whole degrees, so they read the rounded pair.
      const amtRounded = parseInt(dimension.amt);
      const DirRounded = parseInt(dimension.dir);
      let D = parseInt(tracedimensions.direction);

      // Everything past those gates plots from the unrounded pair.
      const exact = (v, fallback) => {
        const n = Number(v);
        return v === "" || v === null || v === undefined || !isFinite(n) ? fallback : n;
      };
      let amt = exact(dimension.amtExact, amtRounded);
      let Dir = exact(dimension.dirExact, DirRounded);

      // Preserve the user-entered values for the Input table (360 shown as 0).
      const DirInput = DirRounded === 360 ? 0 : DirRounded;
      const amtInput = amtRounded;

      // ---- Degenerate geometry (rev.3 27Aug26, Borderline Cases 01Sep26) ---
      // Every case where the main routine would divide by TanAlpha or TanBeta
      // is solved in the tunnel cross-section instead, before anything else
      // runs. The gates read the rounded pair: they test an angle for exactly
      // 0 or 90, which is only ever true of whole degrees, and the solvers
      // take that same pair so the geometry drawn is the one the gate saw.
      const inclinationDeg = parseFloat(tracedimensions.inclination) || 0;
      const Dwrap = D === 360 ? 0 : D;
      const Dirwrap = DirRounded === 360 ? 0 : DirRounded;
      const strikeParallel = alphaBetween(Dwrap, Dirwrap) === 0;
      const orientation = dimension.orientation || "leftinvert";
      const p6 = {
        side: dimension.tunnelside,
        d: dimension.distancefromcenter,
        Hp: dimension.verticalheight,
      };

      // Lines at constant height spanning the whole log.
      const drawLevelTraces = (traces) => {
        const xStart = chainageToCanvasX(logStartRD, padding, multiplier, startOffsetRD);
        const xEnd = chainageToCanvasX(logEndRD, padding, multiplier, startOffsetRD);
        let total = 0;
        traces.forEach((t) => {
          const y = Ymax - t.Ylog * multiplier;
          const seg = new Path.Line(new Point(xStart, y), new Point(xEnd, y));
          seg.style = style;
          total += seg.length;
        });
        return total / multiplier;
      };

      // Draw such traces and record them. No trace means the plane lies on
      // the log's own lines and the alert is the whole output. Either way no
      // single crossing chainage exists: each trace sits at one height for
      // the entire log rather than cutting a named line at one point.
      const finishParallel = ({ traces, alerts, alpha, adip, adipi, asf = 1 }) => {
        drawLevelTraces(traces);
        markDataPoint(dimension, index, style);
        alerts.filter(Boolean).forEach((a) => horizontalAlerts.add(a, index));
        usrvar["Alpha"] = alpha;
        usrvar["BetaHorizontal"] = adip;
        usrvar["Beta"] = adipi;
        usrvar["Inclination"] = inclinationDeg;
        usrvar["Span"] = "∞";
        usrvar["Tracelength"] = "∞";
        ["P1x", "P2x", "P3x", "P4x", "P5x"].forEach((k) => (usrvar[k] = "-"));
        inputRows.push(...buildInputRow(dimension, index, DirInput, amtInput));
        pushOutputRow(index, {
          alpha,
          adip,
          adipi,
          P1x: "-",
          P2x: "-",
          P3x: "-",
          P4x: "-",
          P5x: "-",
          span: usrvar["Span"],
          // A trace on one of the log's own lines is not drawn again but still
          // runs the whole log; only a plane that is a wall leaves none.
          extent: traces.length || alerts.some((a) => LOG_LINE_ALERTS.includes(a))
            ? fullLogExtent(logStartRD, logEndRD)
            : NO_EXTENT,
          // Parallel traces report true spacing directly by convention,
          // unless the branch supplies its own factor.
          asf,
        });
        updateUserVariable(usrvar);
      };

      // Vertical plane with the strike parallel (Borderline Cases §1.1.3).
      // It contains the axis whatever the inclination: through P1/P2 or P3/P4
      // it is the wall itself, elsewhere it cuts the crown along the log.
      if (amtRounded === 90 && strikeParallel) {
        const v = solveVertical({ orientation, geom: geomM, p6 });
        if (v) {
          finishParallel({
            traces: v.traces,
            alerts: [
              v.alert || ALPHA0_PARALLEL_NOTE,
              v.invertInterior && v.alert !== VERTICAL_CENTRELINE_ALERT ? ALPHA0_INVERT_ALERT : null,
            ],
            alpha: 0,
            adip: 0,
            adipi: 0,
          });
          return;
        }
      }

      // Level plane in a horizontal tunnel (Borderline Cases §1.1.1). It has
      // no strike, so this holds whatever direction was typed for it.
      if (amtRounded === 0 && inclinationDeg === 0) {
        const lv = solveLevel({ orientation, geom: geomM, p6 });
        if (lv) {
          finishParallel({
            traces: lv.alert ? [] : lv.traces,
            alerts: [lv.alert || FLAT_PARALLEL_NOTE],
            alpha: alphaBetween(Dwrap, Dirwrap),
            adip: 0,
            adipi: 0,
          });
          return;
        }
      }

      // SUBROUTINE-ALPHA0 — strike parallel, dipping plane, horizontal tunnel.
      if (strikeParallel && inclinationDeg === 0) {
        const a0 = solveAlphaZero({
          D: Dwrap,
          Dir: Dirwrap,
          amt: amtRounded,
          orientation,
          geom: geomM,
          p6,
        });
        if (a0) {
          finishParallel({
            traces: a0.traces,
            alerts: [
              a0.lines ? `${ALPHA0_PARALLEL_NOTE} ${coincidenceAlert(a0.lines)}` : ALPHA0_PARALLEL_NOTE,
              a0.invertInterior ? ALPHA0_INVERT_ALERT : null,
            ],
            alpha: 0,
            adip: 0,
            adipi: 0,
            // Asf = cos(90 - Amt): spacing of the traces across the tunnel.
            // Flat and vertical planes never reach this branch.
            asf: Math.cos(toRadians(90 - amtRounded)),
          });
          return;
        }
      }

      // SUBROUTINE-ALPHA0-INCLINED — strike parallel, or a level plane (which
      // has no strike), in an inclined tunnel. The trace crosses the log once:
      // straight on the walls, curved over the crown, at the apparent dip I.
      if (inclinationDeg !== 0 && (strikeParallel || amtRounded === 0)) {
        const start = crossSectionPoint(orientation, geomM, p6);
        if (start) {
          const dopp = calculateDopp(Dwrap);
          const ai = alphaZeroInclined({
            Xp: start.Xp,
            Hp: start.Hp,
            S: transverseSign(Dwrap, Dirwrap) || 0,
            amt: amtRounded,
            I: inclinationDeg,
            RD0: Number(dimension.rd),
            geom: geomM,
          });
          const union = new Path({
            segments: ai.points.map((p) => [
              chainageToCanvasX(p.RD, padding, multiplier, startOffsetRD),
              Ymax - p.Ylog * multiplier,
            ]),
            style,
          });
          markDataPoint(dimension, index, style);
          clipToLog(union, index);

          const alphaShown = amtRounded === 0 ? alphaBetween(Dwrap, Dirwrap) : 0;
          const extent = polylineExtent(ai.points);
          usrvar["Alpha"] = alphaShown;
          usrvar["BetaHorizontal"] = 0;
          if (amtRounded === 0) horizontalAlerts.add(FLAT_INCLINED_ALERT, index);
          usrvar["Beta"] = Math.abs(inclinationDeg);
          usrvar["TanBeta"] = Math.abs(ai.tanI);
          usrvar["Inclination"] = inclinationDeg;
          usrvar["ApDir"] = inclinationDeg > 0 ? dopp : Dwrap;
          usrvar["Span"] = fmt1(extent.max - extent.min);
          usrvar["Tracelength"] = polylineLength(ai.points);
          Object.keys(ai.crossings).forEach(
            (k) => (usrvar[k + "x"] = ai.crossings[k].toFixed(2))
          );
          inputRows.push(...buildInputRow(dimension, index, DirInput, amtInput));
          pushOutputRow(index, {
            alpha: alphaShown,
            adip: 0,
            adipi: Math.abs(inclinationDeg),
            P1x: ai.crossings.P1,
            P2x: ai.crossings.P2,
            P3x: ai.crossings.P3,
            P4x: ai.crossings.P4,
            P5x: ai.crossings.P5,
            span: usrvar["Span"],
            extent: chainageExtent(extent.min, extent.max),
            asf: computeAsf({
              amt: amtRounded,
              alpha: 0,
              inclination: inclinationDeg,
              apDirTrue: Dwrap,
              dopp,
            }),
          });
          updateUserVariable(usrvar);
          return;
        }
      }

      // A 360° input means 0°.
      D = Dwrap;
      if (Dir === 360) Dir = 0;

      // Calculate Alpha and Beta
      let Alpha = calculateAlpha(D, Dir);
      let TanAlpha = Math.tan(toRadians(Alpha));
      let TanBeta = Math.tan(toRadians(amt)) * Math.sin(toRadians(Alpha));
      let Beta = calculateBeta(amt, Alpha);

      // Calculate quadrants and ApDir
      let quadrants = calculateQuadrants(D);
      let ApDir = calculateApDir(D, Dir, quadrants);
      let Dopp = calculateDopp(D);
      // True apparent dip direction (horizontal reference) — the inclination
      // block below may flip ApDir, but Asf needs the un-flipped one.
      const ApDirTrue = ApDir;

      // Apply Tunnel Inclination 'I' adjustment to Beta and ApDir
      // (algorithm doc, rev.1 12-May-26: Beta'/ApDir block following horizontal Beta)
      let I = parseFloat(tracedimensions.inclination) || 0;
      let BetaOriginal = Beta;
      if (I !== 0) {
        const corrected = correctApparentDip(Beta, ApDir, D, I);
        Beta = corrected.beta;
        ApDir = corrected.apDir;
        TanBeta = Math.tan(toRadians(Beta));
      }

      // ---- SUBROUTINE-PARALLEL-TRACE (rev.3) -------------------------------
      // The inclination correction has cancelled the apparent dip along the
      // axis: the plane contains the axis direction and Subroutine-1/2 would
      // divide by TanBeta = 0. Its trace is a line in the cross-section whose
      // intersections run the whole log; through P1/P3, P2/P4 or P5 with the
      // dip along the tunnel it coincides with the log's own lines
      // (Borderline Cases §1.1.4).
      if (I !== 0 && Math.abs(Beta) < BETA_ZERO_TOLERANCE) {
        const pt = solveParallelTrace({
          D,
          Dir,
          amt,
          alpha: Alpha,
          I,
          orientation,
          geom: geomM,
          p6,
        });
        if (pt) {
          finishParallel({
            traces: pt.alert ? [] : pt.traces,
            alerts: [
              pt.alert || AXIS_PARALLEL_NOTE,
              !pt.alert && pt.invertInterior ? ALPHA0_INVERT_ALERT : null,
            ],
            alpha: Alpha,
            adip: BetaOriginal,
            adipi: 0,
            // Asf = sin(Gamma), Gamma the trace slope in the cross-section
            // (Route C): spacing measured across the tunnel, as in the
            // strike-parallel horizontal case.
            asf: Math.abs(pt.m) / Math.hypot(1, pt.m),
          });
          return;
        }
      }

      // Asf — Apparent Spacing Factor (Algorithm_Additional Xlog3D_Asf_10Aug26)
      const Asf = computeAsf({
        amt,
        alpha: Alpha,
        inclination: I,
        apDirTrue: ApDirTrue,
        dopp: Dopp,
      });
      if (Asf > 1.000001) {
        asfAlerts.add("Apparent Spacing Factor exceeds 1. Check inputs and retry.", index);
      }

      // Calculate RD based on orientation
      if (
        !tracedimensions.iscrown ||
        orientation !== "rightinvert"
      ) {
        rd = (dimension.rd - startOffsetRD) * multiplier;
      } else {
        if (ApDir === D) {
          rd = (dimension.rd - startOffsetRD) * multiplier + hw / TanBeta;
        } else {
          rd = (dimension.rd - startOffsetRD) * multiplier - hw / TanBeta;
        }
      }

      let X1 = padding + rd;
      let P1 = new Point(X1, Ymax);

      // Which way the trace runs across the tunnel follows the strike, so it
      // is read from the level-tunnel ApDir. The inclination can fold the
      // apparent dip past 90° and turn ApDir over; that changes only which
      // way the trace leans up the walls and crown (the hw / TanBeta terms).
      let XP3, XPmid, X2;
      const Diff = ApDirTrue === D ? D - Dir : Dopp - Dir;
      if ((Diff > -360 && Diff < -270) || (Diff > 0 && Diff < 90)) {
        XP3 = P1.x - width / TanAlpha;
      } else {
        XP3 = P1.x + width / TanAlpha;
      }
      XPmid = (P1.x + XP3) / 2;
      X2 = ApDir === D ? P1.x - hw / TanBeta : P1.x + hw / TanBeta;

      let P2 = new Point(X2, Ymax - hw);
      let P3 = new Point(XP3, Ymax - (2 * hw + ArcFull));
      let P4 = new Point(XP3 - hw / TanBeta, Ymax - (hw + ArcFull));
      if (ApDir !== D) {
        P4 = new Point(XP3 + hw / TanBeta, Ymax - (hw + ArcFull));
      }
      let P5;

      // d = 0 is the centreline whichever side was named, and the centreline
      // is classified with "right" at every site below — trace maths,
      // data-point height and legend dot must agree or the plots diverge.
      const p6Side =
        parseFloat(dimension.distancefromcenter) === 0
          ? "center"
          : dimension.tunnelside;

      // Handle P6 (otherpoint) orientation
      if (orientation === "otherpoint") {
        let d = parseFloat(dimension.distancefromcenter);
        // Apply d = 0.01 correction if d == 0
        if (d == 0) {
          d = 0.01;
        }
        let Hp = parseFloat(dimension.verticalheight);

        // Calculate P6 based on tunnel side
        let p6Result;
        if (p6Side === "right" || p6Side === "center") {
          p6Result = calculateP6RightSide(
            dimension.rd - startOffsetRD,
            d,
            Hp,
            D,
            Dir,
            amt,
            Alpha,
            TanAlpha,
            TanBeta,
            quadrants,
            ApDir,
            tracedimensions.width,
            ApDir !== ApDirTrue
          );
        } else {
          p6Result = calculateP6LeftSide(
            dimension.rd - startOffsetRD,
            d,
            Hp,
            D,
            Dir,
            amt,
            Alpha,
            TanAlpha,
            TanBeta,
            quadrants,
            ApDir,
            tracedimensions.width,
            ApDir !== ApDirTrue
          );
        }

        P1 = p6Result.P1;
        P3 = p6Result.P3;
        XPmid = p6Result.XPmid;
        X1 = P1.x;
        XP3 = P3.x;
        X2 = P1.x - (ApDir === D ? hw / TanBeta : -hw / TanBeta);
        P2 = new Point(X2, Ymax - hw);
        P4 = new Point(
          XP3 - (ApDir === D ? hw / TanBeta : -hw / TanBeta),
          Ymax - (hw + ArcFull)
        );
      }

      // Build trace union starting with right wall
      if (!tracedimensions.iscrown) {
        Xmin = Math.min(P1.x, P2.x);
        Xmax = Math.max(P1.x, P2.x);
        traceunion.push([P1.x, P1.y]);
        traceunion.push([P2.x, P2.y]);
      }

      // Right crown trace
      let scalearr = [];
      // Chainage extent of the trace over the crown, from SPL to SPL.
      let H = [],
        XP = [],
        YP = [],
        XP5 = [],
        YP5 = [];

      for (let n = Theta; n <= 90; n++) {
        H[n] = r * Math.sin(toRadians(n));
        if (ApDir === D) {
          if (P2.x > P4.x) {
            XP[n] = P1.x - Math.abs(P1.x - XPmid) * ((n - Theta) / (90 - Theta));
          } else {
            XP[n] = P1.x + Math.abs(P1.x - XPmid) * ((n - Theta) / (90 - Theta));
          }
          XP5[n] = XP[n] - (H[n] + HWn) / TanBeta;
        } else {
          if (P2.x > P4.x) {
            XP[n] = P1.x - Math.abs(XPmid - P1.x) * ((n - Theta) / (90 - Theta));
          } else {
            XP[n] = P1.x + Math.abs(XPmid - P1.x) * ((n - Theta) / (90 - Theta));
          }
          XP5[n] = XP[n] + (H[n] + HWn) / TanBeta;
        }
        YP[n] = Ymax - (hw + Arc * ((n - Theta) / (90 - Theta)));
        YP5[n] = YP[n];
        scalearr.push([XP5[n], YP5[n]]);
        if (Xmin) {
          Xmin = Math.min(Xmin, XP5[n]);
        } else {
          Xmin = XP5[n];
        }
        if (Xmax) {
          Xmax = Math.max(Xmax, XP5[n]);
        } else {
          Xmax = XP5[n];
        }
        traceunion.push([XP5[n], YP5[n]]);
      }

      let rcrown = new Path({
        segments: scalearr,
      });

      // Left crown trace
      scalearr = [];
      H = [];
      XP = [];
      YP = [];
      XP5 = [];
      YP5 = [];

      for (let n = 90; n >= Theta; n--) {
        H[n] = r * Math.sin(toRadians(n));
        if (parseInt(ApDir) === D) {
          if (P2.x > P4.x) {
            XP[n] =
              XP3 +
              Math.abs(XP3 - XPmid) * ((n - Theta) / (90 - Theta));
          } else {
            XP[n] =
              XP3 - Math.abs(XP3 - XPmid) * ((n - Theta) / (90 - Theta));
          }
          XP5[n] = XP[n] - (H[n] + HWn) / TanBeta;
        } else {
          if (P2.x > P4.x) {
            XP[n] =
              XP3 +
              Math.abs(XP3 - XPmid) * ((n - Theta) / (90 - Theta));
          } else {
            XP[n] =
              XP3 - Math.abs(XP3 - XPmid) * ((n - Theta) / (90 - Theta));
          }
          XP5[n] = XP[n] + (H[n] + HWn) / TanBeta;
        }
        YP[n] = Ymax - (hw + ArcFull - Arc * ((n - Theta) / (90 - Theta)));
        YP5[n] = YP[n];
        scalearr.push([XP5[n], YP5[n]]);
        if (Xmin) {
          Xmin = Math.min(Xmin, XP5[n]);
        } else {
          Xmin = XP5[n];
        }
        if (Xmax) {
          Xmax = Math.max(Xmax, XP5[n]);
        } else {
          Xmax = XP5[n];
        }
        traceunion.push([XP5[n], YP5[n]]);
        if (n === 90) {
          P5 = new Point(XP5[n], YP5[n]);
        }
      }

      let lcrown = new Path({
        segments: scalearr,
      });

      // The crown samples step in whole degrees and can stop short of the
      // SPL; the trace's own SPL crossings close the crown span.

      // Left wall trace
      if (!tracedimensions.iscrown) {
        Xmin = Math.min(Xmin, P4.x, P3.x);
        Xmax = Math.max(Xmax, P4.x, P3.x);
        traceunion.push([P4.x, P4.y]);
        traceunion.push([P3.x, P3.y]);
      }

      // Create union path
      let union = new Path({
        segments: traceunion,
        style: style,
      });

      // Calculate trace length
      if (!tracedimensions.iscrown) {
        let tracelength = union["length"] / multiplier;
        usrvar["Tracelength"] = tracelength;
        usrvar["Tracecrown"] =
          (rcrown["length"] + lcrown["length"]) / multiplier;
      } else {
        let tracelength = union["length"] / multiplier;
        usrvar["Tracelength"] = tracelength;
        usrvar["Tracecrown"] = tracelength;
      }

      // Store original RD values for P1-P5 before diff adjustment (for otherpoint orientation)
      // These represent the actual calculated RD coordinates, not affected by visual positioning
      let P1_RD_original = (P1.x - padding) / multiplier + startOffsetRD;
      let P2_RD_original = (P2.x - padding) / multiplier + startOffsetRD;
      let P3_RD_original = (P3.x - padding) / multiplier + startOffsetRD;
      let P4_RD_original = (P4.x - padding) / multiplier + startOffsetRD;
      let P5_RD_original = P5 ? (P5.x - padding) / multiplier + startOffsetRD : null;
      
      // Store original Y coordinates (in RD units) for P1-P5 before diff adjustment
      // Y coordinates are measured from Ymax (which represents Y=0 in RD coordinates)
      let P1_Y_original = 0; // P1 is always at Ymax (Y=0)
      let P2_Y_original = (Ymax - P2.y) / multiplier;
      let P3_Y_original = (Ymax - P3.y) / multiplier;
      let P4_Y_original = (Ymax - P4.y) / multiplier;
      let P5_Y_original = P5 ? (Ymax - P5.y) / multiplier : null;

      // Handle orientation-specific positioning
      usrvar["XPmid"] = (XPmid - padding) / multiplier;
      let diff;
      if (orientation === "centerline") {
        diff = P5.x - P1.x;
        let pointx = union.position.x - diff;
        let pointy = union.position.y;
        union.position = new Point(pointx, pointy);
        let circle = new Path.Circle(new Point(P1.x, P5.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }
      if (orientation === "leftinvert") {
        diff = P3.x - P1.x;
        let pointx = union.position.x - diff;
        let pointy = union.position.y;
        union.position = new Point(pointx, pointy);
        let circle = new Path.Circle(new Point(P1.x, P3.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }
      if (orientation === "leftspl") {
        diff = P4.x - P1.x;
        let pointx = union.position.x - diff;
        let pointy = union.position.y;
        union.position = new Point(pointx, pointy);
        let circle = new Path.Circle(new Point(P1.x, P4.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }
      if (orientation === "rightspl") {
        diff = P2.x - P1.x;
        let pointx = union.position.x - diff;
        let pointy = union.position.y;
        union.position = new Point(pointx, pointy);
        let circle = new Path.Circle(new Point(P1.x, P2.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }
      markKnownChainages(dimension, index);
      if (orientation === "otherpoint") {
        markDataPoint(dimension, index, style);
        // The union is deliberately not re-anchored to the marker: the P6
        // calculators already place P1/P3 at their absolute chainages. An
        // interior point (d < W/2) crosses wall-height Hp at a chainage other
        // than rd, so anchoring there would slide the whole trace off the
        // chainages the Outputs table prints.
      }
      if (orientation === "rightinvert") {
        diff = 0;
        let circle = new Path.Circle(new Point(P1.x, P1.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }

      clipToLog(union, index);

      // Store user variables
      usrvar["RD"] = rd / multiplier;
      usrvar["AMT"] = amt;
      usrvar["Alpha"] = Alpha;
      usrvar["TanAlpha"] = TanAlpha;
      usrvar["Inclination"] = I;
      usrvar["BetaHorizontal"] = BetaOriginal;
      usrvar["Beta"] = Beta;
      usrvar["TanBeta"] = TanBeta;
      usrvar["HWn"] = HWn / multiplier;
      usrvar["Ymax"] = (Ymax - padding) / multiplier;
      usrvar["ApDir"] = ApDir;
     
      
      // For otherpoint orientation, use original RD values (before diff adjustment)
      // For other orientations, use adjusted values (after diff)
      if (orientation === "otherpoint") {
        usrvar["Distancefromcenter"] = parseFloat(dimension.distancefromcenter) === 0 ? 0.01 : dimension.distancefromcenter;
        usrvar["Verticalheight"] = dimension.verticalheight;
        usrvar["P1"] =
          "(" +
          P1_RD_original.toFixed(2) +
          "," +
          P1_Y_original.toFixed(2) +
          ")";
        usrvar["P2"] =
          "(" +
          P2_RD_original.toFixed(2) +
          "," +
          P2_Y_original.toFixed(2) +
          ")";
        usrvar["P3"] =
          "(" +
          P3_RD_original.toFixed(2) +
          "," +
          P3_Y_original.toFixed(2) +
          ")";
        usrvar["P4"] =
          "(" +
          P4_RD_original.toFixed(2) +
          "," +
          P4_Y_original.toFixed(2) +
          ")";
        if (P5 && P5_RD_original !== null && P5_Y_original !== null) {
          usrvar["P5"] =
            "(" +
            P5_RD_original.toFixed(2) +
            "," +
            P5_Y_original.toFixed(2) +
            ")";
        }
      } else {
        usrvar["P1"] =
          "(" +
          ((P1.x - (diff || 0) - padding) / multiplier + startOffsetRD)
            .toFixed(2) +
          ",0)";
        usrvar["P2"] =
          "(" +
          ((P2.x - (diff || 0) - padding) / multiplier + startOffsetRD)
            .toFixed(2) +
          "," +
          (hw / multiplier).toFixed(2) +
          ")";
        usrvar["P3"] =
          "(" +
          ((P3.x - (diff || 0) - padding) / multiplier + startOffsetRD)
            .toFixed(2) +
          "," +
          ((2 * hw + ArcFull) / multiplier).toFixed(2) +
          ")";
        usrvar["P4"] =
          "(" +
          ((P4.x - (diff || 0) - padding) / multiplier + startOffsetRD)
            .toFixed(2) +
          "," +
          ((hw + ArcFull) / multiplier).toFixed(2) +
          ")";
        if (P5) {
          usrvar["P5"] =
            "(" +
            ((P5.x - (diff || 0) - padding) / multiplier + startOffsetRD)
              .toFixed(2) +
            "," +
            ((hw + Arc) / multiplier).toFixed(2) +
            ")";
        }
      }
      usrvar["Span"] =
        "" + (Math.abs(Xmax - Xmin) / multiplier).toFixed(1).replace(/\.0$/, "") + "";

      // Extract X-only chainages for Ch. output
      const extractX = (pStr) => pStr ? pStr.slice(1, pStr.indexOf(",")) : "-";
      usrvar["P1x"] = extractX(usrvar["P1"]);
      usrvar["P2x"] = extractX(usrvar["P2"]);
      usrvar["P3x"] = extractX(usrvar["P3"]);
      usrvar["P4x"] = extractX(usrvar["P4"]);
      usrvar["P5x"] = extractX(usrvar["P5"]);

      inputRows.push(...buildInputRow(dimension, index, DirInput, amtInput));
      pushOutputRow(index, {
        alpha: Alpha,
        adip: BetaOriginal,
        adipi: Beta,
        P1x: usrvar["P1x"],
        P2x: usrvar["P2x"],
        P3x: usrvar["P3x"],
        P4x: usrvar["P4x"],
        P5x: usrvar["P5x"],
        span: usrvar["Span"],
        extent: chainageExtent(
          (Xmin - (diff || 0) - padding) / multiplier + startOffsetRD,
          (Xmax - (diff || 0) - padding) / multiplier + startOffsetRD
        ),
        asf: Asf,
      });

      updateUserVariable(usrvar);
    });

    // Raise each distinct alert once, naming the traces that raised it. Only
    // new or edited traces pop up; the rest are already on the printed list.
    // A redraw of the same traces keeps the pop-ups already showing; any
    // change drops them (and any left over from the input page) first.
    const popUp = changedTraces("dshape", props.dimensions);
    if (popUp.size) removeAllToasts();
    const clipPopUps = clipAlerts.messages(popUp);
    if (clipPopUps.length && window.audio) window.audio.play();
    clipPopUps.forEach((message) => {
      addToast(message, { appearance: "info" });
    });
    horizontalAlerts.messages(popUp).forEach((message) => {
      addToast(message, { appearance: "warning" });
    });
    asfAlerts.messages(popUp).forEach((message) => {
      addToast(message, { appearance: "error" });
    });

    // ---- Tabulated Inputs / Outputs (per 17May26 Trace Page spec) ----
    // Drawing helpers
    const drawBorder = (x1, y1, x2, y2) => {
      const p = new Path.Line(new Point(x1, y1), new Point(x2, y2));
      p.strokeColor = "black";
      p.strokeWidth = 0.5;
      return p;
    };
    const cellText = (text, xL, xR, yT, yB, opts = {}) => {
      const t = new PointText(new Point((xL + xR) / 2, (yT + yB) / 2 + 3));
      t.justification = "center";
      t.fillColor = opts.color || "black";
      if (opts.bold) t.fontWeight = "bold";
      t.fontSize = opts.fontSize || 10;
      t.content = text;
      return t;
    };
    const buildCols = (xStart, defs) => {
      let x = xStart;
      return defs.map((d) => {
        const col = { ...d, left: x, right: x + d.width };
        x += d.width;
        return col;
      });
    };
    const noteLine = (yPos, text, opts = {}) => {
      const n = new PointText(new Point(padding + (opts.indent || 0), yPos));
      n.justification = "left";
      n.fillColor = "black";
      if (opts.bold) n.fontWeight = "bold";
      n.fontSize = opts.fontSize || 9;
      n.content = text;
      return n;
    };

    let curY = points + padding + 2 * (hw + Arc);

    // ----- Inputs section -----
    const inLabel = new PointText(new Point(padding, curY));
    inLabel.justification = "left";
    inLabel.fillColor = "black";
    inLabel.fontWeight = "bold";
    inLabel.content = "Inputs:";
    curY += 18;
    const geomText = new PointText(new Point(padding, curY));
    geomText.justification = "left";
    geomText.fillColor = "black";
    const I0 = parseFloat(tracedimensions.inclination) || 0;
    const D0 = parseInt(tracedimensions.direction);
    geomText.content =
      "Tunnel Direction: " + D0 +
      "; Tunnel Inclination: " + I0 +
      "; Height: " + (height / multiplier) +
      "; Width: " + (width / multiplier) +
      "; Wall Height: " + Number(tracedimensions.hw);
    curY += 20;

    // Input table
    const inColDefs = [
      { key: "no", label: "D.No.", width: 45 },
      { key: "label", label: "Label", width: 45 },
      { key: "dir", label: "DipDir", width: 50 },
      { key: "amt", label: "DipAmt", width: 50 },
      { key: "ch", label: "Chainage", width: 60 },
      { key: "surface", label: "Tunnel Surface", width: 90 },
      { key: "side", label: "Side", width: 45 },
      { key: "dist", label: "Distance", width: 60 },
      { key: "ht", label: "Height", width: 50 },
    ];
    // With every trace sourced from known chainages there is no face heading to
    // report, so the last three columns carry the chainages that were entered.
    const allFromChainages =
      inputRows.length > 0 && inputRows.every((r) => r.source === "chainages");
    const colIdx = (key) => inColDefs.findIndex((c) => c.key === key);
    if (allFromChainages) {
      Object.assign(inColDefs[colIdx("side")], { label: "Ch. Left SPL", width: 65 });
      Object.assign(inColDefs[colIdx("dist")], { label: "Ch. Right SPL", width: 70 });
      Object.assign(inColDefs[colIdx("ht")], { label: "Ch. Centreline", width: 75 });
    }
    const inCols = buildCols(padding, inColDefs);
    const inGroupStartIdx = colIdx("ch"); // "Discontinuity Data Location" group
    const inFaceStartIdx = colIdx("side"); // "Face (Heading)" sub-group
    const inLeftX = inCols[0].left;
    const inRightX = inCols[inCols.length - 1].right;
    const inHeaderRowH = 18;
    const inDataRowH = 18;
    const inHeaderY1 = curY;
    const inHeaderY2 = inHeaderY1 + inHeaderRowH;
    const inHeaderY3 = inHeaderY2 + inHeaderRowH;
    const inDataY0 = inHeaderY3 + inHeaderRowH;
    const inTableBottomY = inDataY0 + inDataRowH * inputRows.length;

    // First 3 cols: tall header cells (span all three header rows)
    inCols.forEach((col, i) => {
      if (i < inGroupStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY1, inDataY0, { bold: true });
      }
    });
    // Group header spanning the location cols
    cellText(
      "Discontinuity Data Location",
      inCols[inGroupStartIdx].left,
      inRightX,
      inHeaderY1,
      inHeaderY2,
      { bold: true }
    );
    // Chainage / Tunnel Surface span the two lower header rows
    inCols.forEach((col, i) => {
      if (i >= inGroupStartIdx && i < inFaceStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY2, inDataY0, { bold: true });
      }
    });
    // "Face (Heading)" sub-group header
    cellText(
      allFromChainages ? "Known Trace Chainages (m)" : "Face (Heading)",
      inCols[inFaceStartIdx].left,
      inRightX,
      inHeaderY2,
      inHeaderY3,
      { bold: true }
    );
    // Face sub-headers (Side / Distance / Height)
    inCols.forEach((col, i) => {
      if (i >= inFaceStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY3, inDataY0, { bold: true });
      }
    });
    // Data rows
    inputRows.forEach((row, r) => {
      const yT = inDataY0 + r * inDataRowH;
      const yB = yT + inDataRowH;
      inCols.forEach((col) => {
        cellText(row[col.key] || "-", col.left, col.right, yT, yB, { color: row.color });
      });
    });
    // Borders — outer rectangle
    drawBorder(inLeftX, inHeaderY1, inRightX, inHeaderY1);
    drawBorder(inLeftX, inTableBottomY, inRightX, inTableBottomY);
    drawBorder(inLeftX, inHeaderY1, inLeftX, inTableBottomY);
    drawBorder(inRightX, inHeaderY1, inRightX, inTableBottomY);
    // Vertical column dividers (start depth depends on grouping)
    inCols.forEach((col, i) => {
      if (i === 0) return;
      let startY = inHeaderY1;
      if (i > inFaceStartIdx) startY = inHeaderY3;
      else if (i > inGroupStartIdx) startY = inHeaderY2;
      drawBorder(col.left, startY, col.left, inTableBottomY);
    });
    // Horizontal divider under the group header
    drawBorder(inCols[inGroupStartIdx].left, inHeaderY2, inRightX, inHeaderY2);
    // Horizontal divider under the "Face (Heading)" header
    drawBorder(inCols[inFaceStartIdx].left, inHeaderY3, inRightX, inHeaderY3);
    // Top of first data row (full width)
    drawBorder(inLeftX, inDataY0, inRightX, inDataY0);
    // Between data rows
    for (let r = 1; r < inputRows.length; r++) {
      const y = inDataY0 + r * inDataRowH;
      drawBorder(inLeftX, y, inRightX, y);
    }
    curY = inTableBottomY + 14;

    // Input notes (bold "Notes:" prefix + body, manually wrapped)
    const inputNotesLabel = noteLine(curY, "Notes:", { bold: true });
    noteLine(curY, " D.No. (Discontinuity number); Label (Discontinuity label); DipDir (Dip direction); DipAmt (Dip amount); Chainage (RD); Tunnel Surface", {
      indent: inputNotesLabel.bounds.width + 2,
    });
    curY += 12;
    const inputNotesEnd = noteLine(
      curY,
      allFromChainages
        ? "(data point location on tunnel surface); Known Trace Chainages: the chainages entered for the trace at left SPL, right SPL and the centreline."
        : "(data point location on tunnel surface); Face (Heading): Side (wrt tunnel centreline), Distance (from centreline), Height (from invert)."
    );
    // Key to the dot that marks each data point on the log.
    const dataPointKeyX = inputNotesEnd.bounds.right + 10;
    const dataPointKeyDot = new Path.Circle(new Point(dataPointKeyX, curY - 3), 2);
    dataPointKeyDot.style = { fillColor: "black", strokeColor: "black", strokeWidth: 1 };
    noteLine(curY, "Data Point", { indent: dataPointKeyX + 5 - padding });
    curY += 22;

    // ----- Outputs section -----
    const outLabel = new PointText(new Point(padding, curY));
    outLabel.justification = "left";
    outLabel.fillColor = "black";
    outLabel.fontWeight = "bold";
    outLabel.content = "Outputs:";
    curY += 18;
    const crownText = new PointText(new Point(padding, curY));
    crownText.justification = "left";
    crownText.fillColor = "black";
    crownText.content = isBoxSection(tracedimensions)
      ? "Box-shaped section: flat roof, no crown arch; Roof width: " + (width / multiplier)
      : "Crown radius: " + usrvar["R"].toFixed(1).replace(/\.0$/, "") +
        "; Crown circumference: " + usrvar["ArcFull"].toFixed(1).replace(/\.0$/, "");
    curY += 20;

    // Output table
    const outColDefs = [
      { key: "no", label: "D.No.", width: 42 },
      { key: "angle", label: "Angle", width: 45 },
      { key: "adip", label: "ADip", width: 45 },
      { key: "adipi", label: "ADipI", width: 50 },
      { key: "chli", label: "ChLI", width: 45 },
      { key: "chri", label: "ChRI", width: 45 },
      { key: "chlspl", label: "ChLSPL", width: 55 },
      { key: "chrspl", label: "ChRSPL", width: 55 },
      { key: "chcl", label: "ChCL", width: 50 },
      { key: "chmin", label: "ChMin", width: 50 },
      { key: "chmax", label: "ChMax", width: 50 },
      { key: "tsp", label: "TSp", width: 45 },
      { key: "asf", label: "Asf", width: 45 },
    ];
    const outCols = buildCols(padding, outColDefs);
    const outLeftX = outCols[0].left;
    const outRightX = outCols[outCols.length - 1].right;
    const outHeaderRowH = 18;
    const outDataRowH = 18;
    const outHeaderY = curY;
    const outDataY0 = outHeaderY + outHeaderRowH;
    const outTableBottomY = outDataY0 + outDataRowH * outputRows.length;

    outCols.forEach((col) => {
      cellText(col.label, col.left, col.right, outHeaderY, outDataY0, { bold: true });
    });
    outputRows.forEach((row, r) => {
      const yT = outDataY0 + r * outDataRowH;
      const yB = yT + outDataRowH;
      outCols.forEach((col) => {
        const v = row[col.key];
        cellText(v != null ? String(v) : "-", col.left, col.right, yT, yB, { color: row.color });
      });
    });
    // Borders
    drawBorder(outLeftX, outHeaderY, outRightX, outHeaderY);
    drawBorder(outLeftX, outTableBottomY, outRightX, outTableBottomY);
    drawBorder(outLeftX, outHeaderY, outLeftX, outTableBottomY);
    drawBorder(outRightX, outHeaderY, outRightX, outTableBottomY);
    outCols.forEach((col, i) => {
      if (i === 0) return;
      drawBorder(col.left, outHeaderY, col.left, outTableBottomY);
    });
    drawBorder(outLeftX, outDataY0, outRightX, outDataY0);
    for (let r = 1; r < outputRows.length; r++) {
      const y = outDataY0 + r * outDataRowH;
      drawBorder(outLeftX, y, outRightX, y);
    }
    curY = outTableBottomY + 14;

    // The persistence caution reads first, directly under the Outputs table.
    const cautionLabel = noteLine(curY, "Caution:", { bold: true });
    noteLine(curY, " Discontinuity trace span may be evaluated with care in consideration of its persistence.", {
      indent: cautionLabel.bounds.width + 2,
    });
    curY += 16;

    const outputAlerts = [...clipAlerts.messages(), ...horizontalAlerts.messages(), ...asfAlerts.messages()];
    if (outputAlerts.length) {
      noteLine(curY, "Alerts:", { bold: true });
      curY += 14;
      outputAlerts.forEach((message) => {
        let line = noteLine(curY, "");
        let words = "";
        message.split(/\s+/).forEach((word) => {
          const candidate = words ? words + " " + word : word;
          line.content = candidate;
          if (line.bounds.width > outRightX - padding && words) {
            line.content = words;
            curY += 12;
            line = noteLine(curY, word);
            words = word;
          } else {
            words = candidate;
          }
        });
        curY += 16;
      });
    }

    // Output abbreviations. Labelled "Abbreviations used" rather than "Notes"
    // to separate it from the notes proper below — the Asf specification calls
    // these two things by those names.
    //
    // The wrap points are hand-placed: the longer label pushes the first line
    // right, so text moved down a line to keep all three within the canvas.
    const outputNotesLabel = noteLine(curY, "Abbreviations used:", { bold: true });
    noteLine(curY, " Angle (Angle between strike and tunnel direction); ADip (Apparent Dip with horizontal);", {
      indent: outputNotesLabel.bounds.width + 2,
    });
    curY += 12;
    noteLine(curY, "ADipI (Apparent Dip with tunnel axis); Trace chainage: ChLI (left invert), ChRI (right invert), ChLSPL (left SPL),");
    curY += 12;
    noteLine(curY, "ChRSPL (right SPL), ChCL (centreline), ChMin (minimum of trace), ChMax (maximum of trace); TSp: Trace span along tunnel;");
    curY += 12;
    noteLine(curY, "Asf: Apparent Spacing Factor.");
    curY += 16;

    // Asf usage notes (Algorithm_Additional Xlog3D_Asf_10Aug26)
    noteLine(curY, "•  Divide True Spacing by the Apparent Spacing Factor Asf to get Apparent Spacing along the tunnel.");
    curY += 12;
    noteLine(curY, "•  True Spacing is the distance between two adjoining discontinuities of the same set across their dip planes. This is");
    curY += 12;
    noteLine(curY, "the shortest distance between the two.", { indent: 11 });
    curY += 12;
    noteLine(curY, "•  Apply Asf for computation of Apparent Thickness and Apparent Aperture along the tunnel.");
    curY += 12;
    // The last note carries a link to the TrueSpace tool. The notes are drawn
    // on the canvas, so this is a canvas item rather than an anchor: blue
    // underlined text, with a near-transparent rectangle over it as the click
    // target — a rectangle hit-tests far more reliably than text glyphs.
    //
    // The URL is spelled out rather than hidden behind wording, because an
    // exported plot is often printed, where nothing is clickable.
    const trueSpaceNote = noteLine(
      curY,
      "•  If True Spacing not available, get True Spacing from this app: "
    );
    const trueSpaceLink = noteLine(curY, TRUESPACE_LABEL, {
      indent: trueSpaceNote.bounds.width + 3,
    });
    trueSpaceLink.fillColor = "#0b57d0";

    const linkUnderline = new Path.Line(
      trueSpaceLink.bounds.bottomLeft.add([0, 1]),
      trueSpaceLink.bounds.bottomRight.add([0, 1])
    );
    linkUnderline.strokeColor = "#0b57d0";
    linkUnderline.strokeWidth = 0.6;

    // Handled by the canvas element rather than a paper item — see
    // projectPointFromEvent for why paper's own hit-testing is off by the
    // device pixel ratio here.
    const box = trueSpaceLink.bounds.expand(6);
    trueSpaceLinkBounds.current = {
      left: box.left,
      right: box.right,
      top: box.top,
      bottom: box.bottom,
    };
    curY += 16;

    paper.view.viewSize = new Size(paper.view.viewSize.width, Math.max(paper.view.viewSize.height, curY + 20));
  };

  const draw = (event) => {
    createScale();
    createLog();
    createTrace();
  };

  const creategraph = () => {
    paper.project.clear();
    let width = Math.max(length + 450, padding + 700);
    let height = Math.max(
      props.dimensions.length * 75 + 480,
      padding + 2 * (hw + Arc) + 430 + props.dimensions.length * 40
    );
    paper.view.viewSize = new Size(width, height);
    draw();
    paper.view.draw();
  };


  const addTrace = () => {
    props.addtrace();
  };

  const showDeleteTrace = () => {
    setShow(true);
  };

  const deleteTrace = () => {
    props.deletetrace(selectedTrace);
    setShow(false);
  };

  const selectTrace = (e) => {
    let value = e.target.value;
    setSelectedTrace(value);
  };

  return (
    <div id="page">
      <Header onBack={props.showinput} />
      <Footer />
      <div className="page-content header-clear-medium">
        <div className="row">
          <div className="col-md-12">
            <button
              onClick={() => {
                props.showinput();
              }}
              className="btn bg-green-dark mw-2 me-3"
            >
              Edit Input
            </button>
            <button onClick={addTrace} className="btn bg-green-dark me-3">
              Add Trace
            </button>
            {props.tracecount > 0 ? (
              <button
                onClick={showDeleteTrace}
                className="btn bg-danger me-3"
              >
                Delete Trace
              </button>
            ) : (
              ""
            )}
            <DownloadMenu filename="dshape-plot" screenLabel="D-shape Tunnel — Plot" feedbackInputs={props.dimensions} />
          </div>
          <div className="col-md-6"></div>
          <div className="col-md-12 overflow-auto">
            {!props.showdebug ? (
              <canvas
                id="paper-canvas"
                resize="true"
                onClick={handleCanvasClick}
                onMouseMove={handleCanvasMouseMove}
              />
            ) : (
              <Debug uservariables={uservariables} />
            )}
          </div>
        </div>
      </div>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Select trace to delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {props.dimensions.map((dimension, index) => (
            <Form.Check
              onChange={selectTrace}
              inline
              value={index}
              style={{ color: colors[index] }}
              label={"Trace " + (index + 1)}
              name="group1"
              type="radio"
              id={index}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" onClick={deleteTrace}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Output;
